// commitmentMath.ts — Canvas Weekly Commitments helpers (May 13, 2026)
//
// TypeScript counterparts to the public.weekly_commitment_match view shape and
// the per-cell state enum. Used by both the audit board (1-on-1 Mode) and the
// RM home Commitment Card.

export type CommitmentState =
  | "committed_showed"   // committed AND a shift overlaps the block window
  | "committed_missed"   // committed AND no shift AND block window has passed
  | "committed_future"   // committed AND no shift AND block window hasn't started yet
  | "orphan_shift"       // NOT committed AND a shift overlaps (off-plan shift)
  | "off_planned";       // NOT committed AND no shift (intentionally off)

export type DayLabel = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
export type BlockLabel = "am" | "pm" | "eve";

export const DAY_LABELS: readonly DayLabel[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const BLOCK_LABELS: readonly BlockLabel[] = ["am", "pm", "eve"];

/** Shape of a single row in public.weekly_commitment_match. */
export interface CommitmentMatchRow {
  commitment_id: string;
  user_id: string;
  week_start: string;        // 'YYYY-MM-DD' (Monday)
  day_label: DayLabel;
  block_label: BlockLabel;
  day_offset: number;        // 0..6
  committed: boolean;
  has_shift: boolean;
  block_starts_at: string;   // ISO timestamptz
  block_ends_at: string;     // ISO timestamptz
  state: CommitmentState;
}

/** Shape of a row in public.weekly_commitments. */
export interface WeeklyCommitment {
  id: string;
  user_id: string;
  week_start: string;
  blocks: BlocksJson;
  areas_by_day: AreasByDay;
  locked_at: string | null;
  locked_by: string | null;
  created_at: string;
  updated_at: string;
}

export type BlocksJson = Partial<Record<DayLabel, Partial<Record<BlockLabel, boolean>>>>;
export type AreasByDay = Partial<Record<DayLabel, string[]>>;

/** An empty 7×3 blocks structure with everything set to false. */
export function emptyBlocks(): BlocksJson {
  const out: BlocksJson = {};
  for (const d of DAY_LABELS) out[d] = { am: false, pm: false, eve: false };
  return out;
}

/** Look up a single block boolean. Defensive against sparse JSON. */
export function isCommitted(blocks: BlocksJson, day: DayLabel, block: BlockLabel): boolean {
  return blocks?.[day]?.[block] === true;
}

/** Aggregate stats across a week's match rows. */
export interface MatchStats {
  committed: number;        // total slots committed
  showed: number;           // committed AND has_shift
  missed: number;           // committed AND !has_shift AND past
  future: number;           // committed AND !has_shift AND upcoming
  orphan: number;           // !committed AND has_shift
  matchPct: number | null;  // showed / (showed + missed), null if no past committed slots
  onPacePct: number | null; // showed / committed, null if committed=0
}

export function aggregateMatch(rows: CommitmentMatchRow[]): MatchStats {
  let committed = 0, showed = 0, missed = 0, future = 0, orphan = 0;
  for (const r of rows) {
    if (r.state === "committed_showed") { committed++; showed++; }
    else if (r.state === "committed_missed") { committed++; missed++; }
    else if (r.state === "committed_future") { committed++; future++; }
    else if (r.state === "orphan_shift") orphan++;
  }
  const past = showed + missed;
  const matchPct  = past > 0 ? Math.round((showed / past) * 100) : null;
  const onPacePct = committed > 0 ? Math.round((showed / committed) * 100) : null;
  return { committed, showed, missed, future, orphan, matchPct, onPacePct };
}

/** Returns the Monday (UTC date string YYYY-MM-DD) of the week containing the given date. */
export function mondayOf(d: Date = new Date()): string {
  const x = new Date(d);
  const isoDow = ((x.getDay() + 6) % 7); // Mon=0..Sun=6
  x.setDate(x.getDate() - isoDow);
  // Use local components to avoid TZ shift in toISOString
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** "May 11 – 17, 2026" formatted from a Monday YYYY-MM-DD. */
export function weekRangeLabel(weekStart: string): string {
  const [y, m, d] = weekStart.split("-").map(Number);
  const start = new Date(y, m - 1, d);
  const end = new Date(y, m - 1, d + 6);
  const mn = (date: Date) => date.toLocaleString("en-US", { month: "short" });
  if (start.getMonth() === end.getMonth()) {
    return `${mn(start)} ${start.getDate()} – ${end.getDate()}, ${y}`;
  }
  return `${mn(start)} ${start.getDate()} – ${mn(end)} ${end.getDate()}, ${y}`;
}

/** Map state → Tailwind class fragments for the cell. */
export function stateClasses(state: CommitmentState): { bg: string; border: string; text: string; label: string } {
  switch (state) {
    case "committed_showed":  return { bg: "bg-emerald-500",  border: "border-emerald-600",  text: "text-white",       label: "showed" };
    case "committed_missed":  return { bg: "bg-slate-300",    border: "border-slate-400",    text: "text-slate-600 line-through", label: "missed" };
    case "committed_future":  return { bg: "bg-orange-500",   border: "border-orange-600",   text: "text-white",       label: "upcoming" };
    case "orphan_shift":      return { bg: "bg-amber-100",    border: "border-amber-500 border-dashed", text: "text-amber-700", label: "orphan" };
    case "off_planned":       return { bg: "bg-white",        border: "border-slate-200",    text: "text-slate-400",   label: "off" };
  }
}

// ────────────────────────────────────────────────────────────────────────
// Pattern detection (Ship 3) — pure functions over multi-week match data.
// ────────────────────────────────────────────────────────────────────────

/** Group match rows by week_start. */
export function groupByWeek(rows: CommitmentMatchRow[]): Map<string, CommitmentMatchRow[]> {
  const m = new Map<string, CommitmentMatchRow[]>();
  for (const r of rows) {
    const arr = m.get(r.week_start);
    if (arr) arr.push(r);
    else m.set(r.week_start, [r]);
  }
  return m;
}

/** Per-week aggregate stats (sorted oldest → newest). */
export interface WeeklyStat {
  week_start: string;
  committed: number;
  showed: number;
  missed: number;
  orphan: number;
  match_pct: number | null;
}

export function weeklyStats(rowsByWeek: Map<string, CommitmentMatchRow[]>): WeeklyStat[] {
  const out: WeeklyStat[] = [];
  for (const [week_start, rows] of rowsByWeek) {
    const s = aggregateMatch(rows);
    out.push({
      week_start,
      committed: s.committed,
      showed: s.showed,
      missed: s.missed,
      orphan: s.orphan,
      match_pct: s.matchPct,
    });
  }
  out.sort((a, b) => a.week_start.localeCompare(b.week_start));
  return out;
}

/** Pattern: a (day, block) slot that was committed_missed in ≥ 3 of the last N weeks. */
export interface RecurringMiss {
  day: DayLabel;
  block: BlockLabel;
  weeks_missed: number;          // count of weeks the slot was missed
  weeks_with_data: number;       // count of weeks we have any data for
  missed_week_starts: string[];  // when it happened
}

/**
 * Finds (day, block) pairs that were committed_missed across ≥ minRecurrence of
 * the last N weeks. Used for the "Friday EVE keeps slipping · 3 of last 4" callout.
 */
export function detectRecurringMisses(
  rowsByWeek: Map<string, CommitmentMatchRow[]>,
  minRecurrence: number = 3,
): RecurringMiss[] {
  // Bucket: "mon|am" → [missed-week-starts]
  const buckets = new Map<string, string[]>();
  for (const [week_start, rows] of rowsByWeek) {
    for (const r of rows) {
      if (r.state === "committed_missed") {
        const key = `${r.day_label}|${r.block_label}`;
        const arr = buckets.get(key);
        if (arr) arr.push(week_start);
        else buckets.set(key, [week_start]);
      }
    }
  }
  const out: RecurringMiss[] = [];
  const weeksWithData = rowsByWeek.size;
  for (const [key, weeks] of buckets) {
    if (weeks.length >= minRecurrence) {
      const [day, block] = key.split("|") as [DayLabel, BlockLabel];
      out.push({
        day,
        block,
        weeks_missed: weeks.length,
        weeks_with_data: weeksWithData,
        missed_week_starts: weeks.sort(),
      });
    }
  }
  // Sort worst-first
  out.sort((a, b) => b.weeks_missed - a.weeks_missed);
  return out;
}

/** Trend direction over the window: improving / declining / stable. */
export type TrendDirection = "up" | "down" | "flat" | "insufficient_data";
export interface TrendResult {
  direction: TrendDirection;
  oldest_pct: number | null;
  newest_pct: number | null;
  diff: number | null;  // newest - oldest (in percentage points)
}

/**
 * Compare the oldest vs newest week's match%. ±5pt threshold to count as a trend.
 * Needs ≥2 weeks with non-null match% to compute.
 */
export function detectTrend(weeklies: WeeklyStat[]): TrendResult {
  const dated = weeklies.filter((w) => w.match_pct != null);
  if (dated.length < 2) {
    return { direction: "insufficient_data", oldest_pct: null, newest_pct: null, diff: null };
  }
  const oldest = dated[0]!.match_pct!;
  const newest = dated[dated.length - 1]!.match_pct!;
  const diff = newest - oldest;
  let direction: TrendDirection = "flat";
  if (diff >= 5)  direction = "up";
  if (diff <= -5) direction = "down";
  return { direction, oldest_pct: oldest, newest_pct: newest, diff };
}

/** A specific (day, block) slot. */
export interface SlotKey {
  day: DayLabel;
  block: BlockLabel;
}
export interface SlotPerformance extends SlotKey {
  committed: number;
  showed: number;
  show_rate: number; // 0..100
}

/**
 * Per-slot show rate across the window. Slots with no committed cells are excluded.
 * Returns sorted descending by show_rate.
 */
export function slotPerformance(rowsByWeek: Map<string, CommitmentMatchRow[]>): SlotPerformance[] {
  const acc = new Map<string, { day: DayLabel; block: BlockLabel; committed: number; showed: number }>();
  for (const rows of rowsByWeek.values()) {
    for (const r of rows) {
      if (!r.committed) continue;
      const key = `${r.day_label}|${r.block_label}`;
      const cur = acc.get(key) ?? { day: r.day_label, block: r.block_label, committed: 0, showed: 0 };
      cur.committed += 1;
      if (r.state === "committed_showed") cur.showed += 1;
      acc.set(key, cur);
    }
  }
  const out: SlotPerformance[] = [];
  for (const v of acc.values()) {
    out.push({
      day: v.day,
      block: v.block,
      committed: v.committed,
      showed: v.showed,
      show_rate: v.committed > 0 ? Math.round((v.showed / v.committed) * 100) : 0,
    });
  }
  out.sort((a, b) => b.show_rate - a.show_rate || b.committed - a.committed);
  return out;
}

/** Pretty-print a slot key, e.g. "Fri EVE". */
export function slotLabel(s: SlotKey): string {
  const day = s.day.charAt(0).toUpperCase() + s.day.slice(1);
  const block = s.block === "eve" ? "EVE" : s.block.toUpperCase();
  return `${day} ${block}`;
}
